import { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { SyntheticEvent } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';

import { PasswordField } from '../../components/PasswordField';
import { describeErrors } from '../../utils/back-errors.util';
import { AuthenticationContextProvider_AuthenticatedUser_QueryDocument } from '../../contexts/AuthenticationContext';
import { AlertMessages } from '../../components/AlertMessages';

import { useUpdateMyDataMutation } from './hooks/useUpdateMyDataMutation.hook';
import { myDataForm2ApiMapper } from './mappers/my-data.form2api.mapper';
import { MyDataSchema } from './schemas/my-data.schema';
import type { MyDataFormValues } from './types/my-data-form-values.type';

interface Props {
  readonly email: string;
  readonly onClose: (event?: Readonly<SyntheticEvent>) => void;
  readonly onUpdated?: () => void;
}

const PASSWORD_FIELDS = [
  {
    name: 'newPassword',
    label: 'Nouveau mot de passe',
    helperText: 'Laisser vide pour conserver le mot de passe actuel',
    autoComplete: 'new-password',
  },
  {
    name: 'newPasswordConfirmation',
    label: 'Confirmation du nouveau mot de passe',
    helperText: null,
    autoComplete: 'new-password',
  },
  {
    name: 'currentPassword',
    label: 'Mot de passe actuel',
    helperText: 'Requis pour confirmer la modification',
    autoComplete: 'current-password',
  },
] as const;

export const MyDataDialog = ({
  email,
  onClose,
  onUpdated,
}: Readonly<Props>) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync, error } = useUpdateMyDataMutation();

  const initialValues = useMemo<MyDataFormValues>(
    () => ({
      email,
      newPassword: EMPTY_STRING,
      newPasswordConfirmation: EMPTY_STRING,
      currentPassword: EMPTY_STRING,
    }),
    [email],
  );

  const handleSubmit = useCallback(
    async (values: Readonly<MyDataFormValues>) => {
      await mutateAsync(myDataForm2ApiMapper(values, email))
        .then(() => {
          enqueueSnackbar('Vos données ont bien été mises à jour.');
          onUpdated?.();
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
              ),
            ],
          });
          onClose();
        })
        .catch(() => undefined);
    },
    [email, enqueueSnackbar, mutateAsync, onClose, onUpdated, queryClient],
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const formik = useFormik<MyDataFormValues>({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema: MyDataSchema,
  });

  const errorOf = (name: keyof MyDataFormValues) =>
    formik.touched[name] ? formik.errors[name] : undefined;

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open>
      <form noValidate onSubmit={formik.handleSubmit}>
        <DialogTitle>Modifier Mes Données</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error ? (
              <AlertMessages
                messages={describeErrors(error)}
                severity="error"
              />
            ) : null}
            <TextField
              autoComplete="email"
              error={!!errorOf('email')}
              fullWidth
              helperText={errorOf('email')}
              id="my-data-email"
              label="Adresse e-mail"
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              required
              type="email"
              value={formik.values.email}
            />
            {PASSWORD_FIELDS.map(
              ({ name, label, helperText, autoComplete }) => (
                <PasswordField
                  autoComplete={autoComplete}
                  error={!!errorOf(name)}
                  fullWidth
                  helperText={errorOf(name) ?? helperText}
                  id={`my-data-${name}`}
                  key={name}
                  label={label}
                  name={name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  required={name === 'currentPassword'}
                  value={formik.values[name]}
                />
              ),
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} size="large" variant="outlined">
            Annuler
          </Button>
          <Button
            disabled={formik.isSubmitting}
            size="large"
            type="submit"
            variant="contained"
          >
            Modifier
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
